/*
 * window.fetch wrapper for interceptors and request method generators.
 */
import Cache, { isCacheType } from '../cache';
import Authorization from '../helpers/JWT';
import Coerce from '../helpers/coerce';

let defaultOptions = {
    request: { // Default fetch options.
        mode: 'cors',
        credentials: 'omit',
        timeout: 30000,
        reject: true,
    },
    url: {}, // Default URL replacements.
},
    // Allow to reset options with `FetchUtils.setDefaultOptions(true)`
    initialOptions = { ...defaultOptions };

export class FetchUtils {
    constructor(interceptors = {}) {
        this.interceptors = Coerce.Object(interceptors);
        this.settings = {
            url: false,
            options: Coerce.Object(defaultOptions.request),
            Request: false,
            Headers: new Headers(),
        };

        // Create an AbortController. - https://developer.mozilla.org/en-US/docs/Web/API/AbortController
        this.controller = new AbortController();
        this.abort = () => { this.controller.abort() };
    }

    static setDefaultOptions(opts = {}, promisify = false) {
        defaultOptions.request = opts === true ?
            { ...initialOptions.request }
            :
            {
                ...Coerce.Object(defaultOptions.url),
                ...Coerce.Object(opts),
            };

        return (promisify ?
            Promise.resolve(defaultOptions.request)
            :
            defaultOptions.request
        );
    }
    setDefaultOptions(...args) {
        return this.constructor.setDefaultOptions.apply(this, args);
    }

    static setDefaultURLParams(opts = {}, promisify = false) {
        defaultOptions.url = opts === true ?
            { ...initialOptions.url }
            :
            {
                ...Coerce.Object(defaultOptions.url),
                ...Coerce.Object(opts),
            };

        return (promisify ?
            Promise.resolve(defaultOptions.url)
            :
            defaultOptions.url
        );
    }
    setDefaultURLParams(...args) { return this.constructor.setDefaultURLParams.apply(this, args); }

    setInterceptors(interceptors = false) {
        this.interceptors = {
            ...(interceptors || {}),
        };

        return this;
    }

    getInterceptors(type = 'response') {
        return this.interceptors[type] || [function () { return [...arguments] }];
    }

    runInterceptor(interceptor, args) {
        try {
            return interceptor.apply(this, args);
        } catch {
            return args;
        }
    }

    responseInterceptor(interceptor, args, original) {
        try {
            let value = interceptor.apply(this, [...args, original]),
                [
                    data,
                    Resp = original,
                ] = (Array.isArray(value) ? value : [value]);

            return [
                data,
                !(Resp instanceof Response) ?
                    new Response(new Blob([JSON.stringify(data)], { type: 'application/json' }), Resp)
                    :
                    Resp,
            ];
        } catch {
            return args;
        }
    }

    static format([url, options = {}]) {
        let replacements = {
            ...Coerce.Object(defaultOptions.url),
            ...Coerce.Object(options),
        };

        return (url = String(url)).replace(/\/?{(?:\s+)?(.+?)(?:\s+)?}/gi, (fullKey, _) => {
            let [key, fallback] = (_.trim().match(/^(.[^|]+)\|\|?(.*)/) || [0, _]).slice(1).map(k => k.trim()),
                value = (replacements[key] !== undefined ? replacements[key] : (fallback || ''));

            return `${value !== '' && fullKey[0] == '/' ? '/' : ''}${value}`;
        });
    }

    format(...args) { return this.constructor.format.apply(this, args); }

    URL(url = false, _options = {}) {
        // Create URL object. - https://developer.mozilla.org/en-US/docs/Web/API/URL/URL
        if (url) {
            let {
                searchParams = {},
                origin = window.location.origin,
            } = {
                ...Coerce.Object(defaultOptions.url),
                ...Coerce.Object(_options),
            }

            // Format the URL with replacements.
            this.settings.url = new URL(this.format(Coerce.Array(url)), origin);

            // Create GET search params. - https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams
            Object.entries(searchParams).forEach(([key, val]) => {
                if (val !== undefined) this.settings.url.searchParams.set(key, val);
            });
        }

        return this.settings.url || false;
    }

    create(_options = false, headers = false) {
        let {
            abort,
            retry = true,
            ...options
        } = {
            ...Coerce.Object(defaultOptions.request),
            ...Coerce.Object(_options, this.settings.options),
        }

        // Setup retries.
        if (retry && !this.retrying) this.settings.retries = {
            time: 250,
            maximum: 3,
            ...(retry == true ? {} : retry),
            current: 1,
        };

        // Use a custom AbortController. - https://developer.mozilla.org/en-US/docs/Web/API/AbortController
        if (abort) this.controller = abort;

        // Run request interceptors before building the Request() object.
        let [
            requestURL = this.settings.url,
            requestOptions = Coerce.Object(options, Coerce.Object(this.settings.options)),
        ] = this.getInterceptors('request')?.reduce?.((acc, interceptor) => this.runInterceptor(interceptor, acc), [
            this.settings.url,
            Coerce.Object(options, Coerce.Object(this.settings.options)),
            Coerce.Object(this.settings.retries, false),
        ]) || [];

        // Create request options.
        this.settings.options = {
            ...this.settings.options,
            ...requestOptions,
        };

        // Create request headers. - https://developer.mozilla.org/en-US/docs/Web/API/Headers
        headers = headers || this.settings.options.headers || this.settings.options.Headers || false;
        if (headers) {
            this.settings.Headers = headers instanceof Headers ? headers : new Headers(headers);
        }

        // Pass JWT auth token if it exists and appears to be valid.
        // XXX: Validate expire times?
        let {
            origin,
        } = requestURL;
        if (!this.settings.Headers.has('Authorization') && Authorization.isAuthed(origin)) {
            this.settings.Headers.set('Authorization', Authorization.get(origin).packet);
        }

        // Create request object. - https://developer.mozilla.org/en-US/docs/Web/API/Request
        this.settings.Request = new Request(requestURL, {
            ...this.settings.options,
            signal: this.controller.signal,
            headers: this.settings.Headers,
        });

        return this;
    }

    retry(url = false, options = false) {
        if (!this.settings.retries) return Promise.reject();

        this.retrying = true;
        if (++this.settings.retries.current <= this.settings.retries.maximum) {
            return new Promise((resolve) => {
                setTimeout(resolve, (this.settings.retries.time = ((this.settings.retries.time || 50) * 1.5)));
            }).then(() => this.fulfill(url, options)).then((...args) => {
                this.retrying = false;
                return Promise.resolve(...args);
            });
        } else {
            this.retrying = false;
            return Promise.reject(new Error(`Retry limit reached: ${this.settings.retries.maximum}`));
        }
    }

    hash(str) {
        return `hash:${Array.from(str).reduce((s, c) => Math.imul(31, s) + c.charCodeAt(0) | 0, 0)}`;
    }

    static addReader(response, options = {}) {
        response.read = ((_response, optType) => {
            return async (type = optType) => {
                if (!_response.bodyUsed) {
                    try {
                        if (!type) {
                            let [cts] = _response.headers?.get?.('Content-Type')?.match?.(/^[-\w.+]+\/[-\w.+][^;]+/) || [false];

                            if (cts) {
                                [, type] = ([
                                    ['application/json', 'json'],
                                    ['text/x-json', 'json'],
                                    ['text/', 'text'],
                                ].find(([ct]) => cts.indexOf(ct) === 0) || [null, 'blob']);
                            }
                        }
                        _response.readBody = await _response[type || 'blob']();
                    } catch {
                        _response.readBody = false;
                    }
                }
                return Promise.resolve(_response.readBody)
            }
        })(response, options?.type);

        return response;
    }
    addReader(...args) {
        return this.constructor.addReader.apply(this, args);
    }

    // Initiate the fetch request.
    async fulfill(_url = false, _options = false) {
        let url = this.URL(_url, {
            ...Coerce.Object(defaultOptions.request),
            ...Coerce.Object(_options),
        });

        try {
            let {
                cache = false,
                ttl = 300000,
                cacheMechanism = 'session',
                timeout = this.settings.options.timeout,
                ...options
            } = Coerce.Object(_options);

            if (cache === true || (cache !== false && !isNaN(cache))) {
                // If you change the cacheMechanism you'll lose your old cache.
                if (isCacheType(cacheMechanism) && window.FetchCache?.mechanism !== cacheMechanism) {
                    window.FetchCache?.clear?.();
                    window.FetchCache = new Cache(10, cacheMechanism, 'fetch-utils');
                }
                ttl = (cache !== true ? parseInt(cache, 10) : ttl);
                cache = window.FetchCache;
            }

            let {
                Request,
                options: {
                    manual = false,
                    reject = true,
                },
            } = ((url || options) ? this.create(options) : this).settings;

            let {
                origin = undefined,
            } = Coerce.Object(this.settings.url);

            let hashed = Request.url;
            if (cache?.isCache) {
                try {
                    hashed = this.hash(Request.url);
                } catch { }
                if (cache.has(hashed)) {
                    let cached = cache.get(hashed, true) || undefined;
                    if (cached !== undefined) {
                        return Promise.resolve(new FetchResult(cached, null, null, Authorization.get(origin), true));
                    }
                }
            }

            if (timeout) setTimeout(() => {
                this.timedout = true;
                this.abort();
            }, timeout);

            let response = this.addReader(await fetch(Request), this.settings.options);

            if (!response.ok) {
                if (response.status === 401) {
                    Authorization.remove(origin);
                    this.settings.retries = false;
                }
                if (reject) {
                    return this.retry(url, options).catch(() => Promise.reject(this.getInterceptors('responseError')?.reduce?.((acc, interceptor) => {
                        return this.runInterceptor(interceptor, acc)[0];
                    }, [response])));
                }
            }

            // Attempt to handle JWT.
            if (response.headers?.has('Authorization')) {
                Authorization.set(origin, response.headers.get('Authorization'));
            }

            let result = (response.ok ? Promise.reject() : this.retry(url, options)).catch(async () => {
                return new FetchResult(...this.getInterceptors('response').reduce((acc, interceptor) => {
                    return this.responseInterceptor(interceptor, acc, response);
                }, [manual ? response : await response.read(), response]), response.headers, Authorization.get(origin))
            });

            if (cache?.isCache && !result.isBlob) {
                cache.set(hashed, result.data, ttl);
            }

            return Promise.resolve(result);
        } catch ({ message }) {
            return this.retry(url, _options).catch(() => {
                return Promise.reject(this.getInterceptors(this.timedout ? 'timeout' : 'requestError')?.reduce?.((acc, interceptor) => {
                    return this.runInterceptor(interceptor, acc)[0]
                }, [
                    this.timedout ? new TimeoutError('Request has timed out.') : this.addReader(new Response(new Blob([message], { type: 'text/plain' }), {
                        status: 400,
                        statusText: String(message).toUpperCase(),
                    }, this.settings.options)),
                ]));
            });
        }
    }
}

// Export to check for timeouts in catch() calls.
export class TimeoutError extends Error {
    constructor(message) {
        super(message);
        this.status = 400;
        this.name = 'TimeoutError';
    }
}

/*
 * FetchResult object to support both Array and Object spread.
 *
 *   let [ data ] = await FetchIt('/api/v1/foo', { ... })
 *      or
 *   let { data } = await FetchIt('/api/v1/foo', { ... })
 */
export class FetchResult extends Array {
    get Auth() { return this[3] }
    get data() { return this[0] }
    get Headers() { return this[2] }
    get Object() { return this.toObject() }
    get Response() { return this[1] }

    get isBlob() { return this[0] instanceof Blob }
    get isCache() { return this[4] || false }

    toObject() {
        return {
            data: this[0],
            Response: this[1],
            Headers: this[2],
            Auth: this[3],
        }
    }
}

/*
 * Generate a mapping of all available request methods.
 * The mappings are exported as uppercase functions.
 *
 *  import { GET } from '@corefact/toolchain/fetch-utils';
 *
 *  GET('https://example.com/example.json').then((data) => ...)
 *      OR
 *  RequestMethods.get('https://example.com/example.json').then((data) => ...)
 */
export let RequestMethods = (() => {
    let modifiers = {
        strip: (options = {}, headers = {}) => ({
            ...options,
            body: undefined,
            headers: options.headers === false ? undefined : {
                ...headers,
                ...Coerce.Object(options.headers),
            },
        }),
        stringify: (options = {}, headers = {}) => ({
            ...{
                ...options,
                stringify: undefined,
            },
            body: options.stringify === false ?
                options.body
                :
                JSON.stringify(Coerce.Object(options.body)),
            headers: options.headers === false ? undefined : {
                ...headers,
                ...Coerce.Object(options.headers),
            },
        }),
        text: (options = {}) => ({
            ...options,
            type: 'text',
        }),
    };
    return Object.fromEntries([
        ...[
            [
                'delete',
                modifiers.stringify,
            ],
            [
                'get',
                modifiers.strip,
            ],
            [
                'head',
                [
                    modifiers.strip,
                    modifiers.text,
                ],
                {},
                (result) => result?.Headers,
            ],
            [
                'options',
                [
                    modifiers.stringify,
                    modifiers.text,
                ],
                {},
                (result) => result?.Headers,
            ],
            [
                'patch',
                modifiers.stringify,
                { 'Content-Type': 'application/json' },
            ],
            [
                'post',
                modifiers.stringify,
                { 'Content-Type': 'application/json' },
            ],
            [
                'put',
                modifiers.stringify,
                { 'Content-Type': 'application/json' },
            ],
        ].map(([method, modifiers, headers = {}, _result = (result) => result?.data]) => [
            method,
            (url, _options = {}, interceptors = {}) => {
                let {
                    result = _result,
                    ...options
                } = Coerce.Object(_options)
                return FetchIt(url, Array.isArray(modifiers) ?
                    modifiers.reduce((acc, modifier) => modifier({ ...acc, ...options, method }, headers), {})
                    :
                    modifiers({ ...options, method }, headers)
                    , interceptors).then(result);
            },
        ]),
        [
            'multipart',
            async (url, form = new FormData()) => {
                return await (await fetch(FetchUtils.format(Coerce.Array(url)), { method: 'POST', body: form })).text()
            },
        ],
        [
            'useAuthorization',
            async (ignore = false, endpoint = '/api/v3/session/verify') => {
                try {
                    let opts = {
                        retry: false,
                        credentials: 'include',
                        result: (result) => Coerce.Object(result?.Auth?.decoded?.payload),
                    };
                    if (ignore) {
                        return await Promise.all([
                            RequestMethods.get(endpoint, opts),
                        ].map(p => p.catch(e => e)));
                    } else {
                        return RequestMethods.get(endpoint, opts);
                    }
                } catch {
                    return Promise.reject(new Response(new Blob(), {
                        'status': 401,
                        'statusText': 'UNAUTHORIZED',
                    }));
                }
            },
        ],
    ]);
})();

export const {
    DELETE = RequestMethods.delete,
    GET = RequestMethods.get,
    HEAD = RequestMethods.head,
    OPTIONS = RequestMethods.options,
    PATCH = RequestMethods.patch,
    POST = RequestMethods.post,
    PUT = RequestMethods.put,
    MultiPart = RequestMethods.multipart,
    useAuthorization = RequestMethods.useAuthorization,
} = RequestMethods;

// Wrap fetch() to support interceptors.
export default async function FetchIt(url, options = {}, interceptors = {}) {
    return await new FetchUtils(interceptors).fulfill(url, options);
}
